/**
 * Entry quick view — a right-hand drawer with the person, their answers and the next
 * actions (approve / reject / confirm / promote / cancel). Used by the entries grid,
 * the approval queue and the dashboard so reviewing never means losing your place.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  IconCheck,
  IconX,
  IconBan,
  IconCircleCheck,
  IconArrowUp,
  IconExternalLink,
  IconMail,
  IconPhone,
  IconAlertTriangle,
  IconCopy,
} from '@tabler/icons-react';
import {
  Drawer,
  Stack,
  Group,
  Text,
  Button,
  Badge,
  Alert,
  Skeleton,
  Textarea,
  Switch,
  Paper,
  Anchor,
} from '@/components/ui';
import { Divider } from '@/components/ui/data-display';
import { useFormEntriesService, type FormEntryDetailView } from '@actschurch/ccms-sdk';
import { toast } from '@/components/ui/providers/AlertProvider';
import { useFormsPermissions } from '@/hooks/forms/useFormsPermissions';
import { EntryStatusBadge, SourceBadge, PaymentStatusBadge } from './FormsBadges';
import { EntryAnswers } from './EntryAnswers';
import { PersonAvatar, RefChip, RelativeTime } from './FormsUi';
import { entryPersonName, entryStatusLabel } from './formsDisplay';
import { entryPath, errorMessage, formatMoney } from './formsUtils';
import { formsKeys } from './useFormsLookups';

export type QuickAction = 'approve' | 'reject' | 'confirm' | 'promote' | 'cancel';

const ACTIONS: Record<QuickAction, { label: string; color: string; icon: React.ReactNode; variant: 'filled' | 'light' }> = {
  approve: { label: 'Approve', color: 'green', icon: <IconCheck size={16} />, variant: 'filled' },
  confirm: { label: 'Confirm', color: 'teal', icon: <IconCircleCheck size={16} />, variant: 'light' },
  promote: { label: 'Offer a place', color: 'grape', icon: <IconArrowUp size={16} />, variant: 'light' },
  reject: { label: 'Reject', color: 'red', icon: <IconX size={16} />, variant: 'light' },
  cancel: { label: 'Cancel entry', color: 'gray', icon: <IconBan size={16} />, variant: 'light' },
};

const ALIASES: Record<string, QuickAction> = { approve: 'approve', reject: 'reject', confirm: 'confirm', promote: 'promote', cancel: 'cancel' };

interface EntryQuickViewProps {
  entryId: number | null;
  onClose: () => void;
  /** Keep the drawer open after an action (the approval queue moves to the next entry). */
  onActionDone?: (entry: FormEntryDetailView, action: QuickAction) => void;
  /** Row-level rights for the form, when known (entries tab). */
  canApprove?: boolean;
  canEdit?: boolean;
}

export function EntryQuickView({ entryId, onClose, onActionDone, canApprove, canEdit }: EntryQuickViewProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const entriesService = useFormEntriesService();
  const perms = useFormsPermissions();
  const [pending, setPending] = useState<QuickAction | null>(null);
  const [note, setNote] = useState('');
  const [silent, setSilent] = useState(false);

  useEffect(() => {
    setPending(null);
    setNote('');
    setSilent(false);
  }, [entryId]);

  const { data: entry, isLoading, error } = useQuery({
    queryKey: formsKeys.entry(entryId ?? 0),
    queryFn: () => entriesService.getById(entryId!),
    enabled: !!entryId,
  });

  const mayApprove = canApprove ?? perms.canApprove;
  const mayEdit = canEdit ?? perms.canEditEntries;

  const actions = useMemo(() => {
    const allowed = new Set<QuickAction>();
    for (const a of entry?.allowedActions ?? []) {
      const k = ALIASES[a.replace(/[^a-z]/gi, '').toLowerCase()];
      if (k) allowed.add(k);
    }
    return (['approve', 'confirm', 'promote', 'reject', 'cancel'] as QuickAction[]).filter((a) => {
      if (!allowed.has(a)) return false;
      if (a === 'approve' || a === 'reject') return mayApprove;
      return mayEdit || mayApprove;
    });
  }, [entry?.allowedActions, mayApprove, mayEdit]);

  const run = useMutation({
    mutationFn: (action: QuickAction) => {
      const req = { note: note.trim() || undefined, silent };
      switch (action) {
        case 'approve':
          return entriesService.approve(entryId!, req);
        case 'reject':
          return entriesService.reject(entryId!, req);
        case 'confirm':
          return entriesService.confirm(entryId!, req);
        case 'promote':
          return entriesService.promote(entryId!, req);
        case 'cancel':
        default:
          return entriesService.cancel(entryId!, req);
      }
    },
    onSuccess: (updated, action) => {
      queryClient.setQueryData(formsKeys.entry(updated.formEntryID), updated);
      queryClient.invalidateQueries({ queryKey: ['forms', 'entries'] });
      queryClient.invalidateQueries({ queryKey: ['forms', 'detail'] });
      queryClient.invalidateQueries({ queryKey: formsKeys.dashboard() });
      queryClient.invalidateQueries({ queryKey: ['forms', 'analytics'] });
      toast.success(ACTIONS[action].label, `${entryPersonName(updated)} is now ${entryStatusLabel(updated.statusID, updated.statusName).toLowerCase()}.`);
      setPending(null);
      setNote('');
      setSilent(false);
      onActionDone?.(updated, action);
    },
    onError: (err) => toast.error('Action failed', errorMessage(err)),
  });

  const name = entry ? entryPersonName(entry) : '';
  const balance = entry ? Math.max(entry.amountDue - entry.amountPaid, 0) : 0;

  return (
    <Drawer
      opened={!!entryId}
      onClose={onClose}
      position="right"
      size="lg"
      padding="lg"
      title={<Text fw={700}>Entry</Text>}
    >
      {isLoading || !entry ? (
        error ? (
          <Alert color="red" icon={<IconAlertTriangle size={16} />}>{errorMessage(error, 'Could not load the entry')}</Alert>
        ) : (
          <Stack gap="md">
            <Skeleton height={64} radius="md" />
            <Skeleton height={36} radius="md" />
            <Skeleton height={280} radius="md" />
          </Stack>
        )
      ) : (
        <Stack gap="lg">
          {/* Who */}
          <Group wrap="nowrap" align="flex-start" gap="md">
            <PersonAvatar name={name} size="lg" avatarKey={entry.email || name} />
            <Stack gap={4} style={{ minWidth: 0, flex: 1 }}>
              <Text fw={700} size="lg" lineClamp={1}>{name}</Text>
              <Group gap="md" wrap="wrap">
                {entry.email && (
                  <Anchor href={`mailto:${entry.email}`} size="sm" c="dimmed">
                    <Group gap={4} wrap="nowrap"><IconMail size={14} />{entry.email}</Group>
                  </Anchor>
                )}
                {entry.mobile && (
                  <Anchor href={`tel:${entry.mobile.replace(/[^\d+]/g, '')}`} size="sm" c="dimmed">
                    <Group gap={4} wrap="nowrap"><IconPhone size={14} />{entry.mobile}</Group>
                  </Anchor>
                )}
              </Group>
              <Group gap={6} wrap="wrap" mt={4}>
                <EntryStatusBadge statusId={entry.statusID} name={entry.statusName} />
                <SourceBadge sourceId={entry.sourceID} name={entry.sourceName} />
                {entry.paymentStatusID > 1 && <PaymentStatusBadge statusId={entry.paymentStatusID} name={entry.paymentStatusName} />}
                {entry.isDuplicate && (
                  <Badge color="orange" variant="light" leftSection={<IconCopy size={12} />} styles={{ root: { textTransform: 'none' } }}>
                    Possible duplicate
                  </Badge>
                )}
                {entry.waitlistPosition ? (
                  <Badge color="grape" variant="light" styles={{ root: { textTransform: 'none' } }}>#{entry.waitlistPosition} on the waitlist</Badge>
                ) : null}
              </Group>
            </Stack>
          </Group>

          <Paper withBorder radius="md" p="sm">
            <Group justify="space-between" wrap="wrap" gap="xs">
              <Group gap={6}>
                <Text size="xs" c="dimmed">Reference</Text>
                <RefChip value={entry.publicRef} />
              </Group>
              <Group gap={6}>
                <Text size="xs" c="dimmed">Submitted</Text>
                <RelativeTime stamp={entry.createdDateTime} size="xs" />
              </Group>
              {entry.formName && (
                <Group gap={6}>
                  <Text size="xs" c="dimmed">Form</Text>
                  <Text size="xs" fw={600} lineClamp={1}>{entry.formName}</Text>
                </Group>
              )}
              {entry.amountDue > 0 && (
                <Group gap={6}>
                  <Text size="xs" c="dimmed">Paid</Text>
                  <Text size="xs" fw={600}>{formatMoney(entry.amountPaid)} of {formatMoney(entry.amountDue)}</Text>
                  {balance > 0 && <Badge size="xs" color="orange" variant="light">{formatMoney(balance)} due</Badge>}
                </Group>
              )}
            </Group>
          </Paper>

          {entry.reviewNote && (
            <Alert color="blue" title="Review note">{entry.reviewNote}</Alert>
          )}

          {/* Next steps */}
          {actions.length > 0 && (
            <Stack gap="xs">
              <Group gap="xs" wrap="wrap">
                {actions.map((a) => (
                  <Button
                    key={a}
                    size="sm"
                    color={ACTIONS[a].color}
                    variant={pending === a ? 'filled' : ACTIONS[a].variant}
                    leftSection={ACTIONS[a].icon}
                    onClick={() => setPending(pending === a ? null : a)}
                  >
                    {ACTIONS[a].label}
                  </Button>
                ))}
              </Group>
              {pending && (
                <Paper withBorder radius="md" p="md" bg={`var(--mantine-color-${ACTIONS[pending].color}-light)`}>
                  <Stack gap="sm">
                    <Textarea
                      label={pending === 'reject' ? 'Reason (shared with the person)' : 'Note (optional)'}
                      placeholder={pending === 'reject' ? 'e.g. The event is for Grades 7–12 only' : 'Visible in the entry history'}
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      autosize
                      minRows={2}
                    />
                    <Group justify="space-between" wrap="wrap">
                      <Switch label="Don't notify the person" checked={silent} onChange={(e) => setSilent(e.currentTarget.checked)} />
                      <Group gap="xs">
                        <Button variant="subtle" color="gray" onClick={() => setPending(null)}>Back</Button>
                        <Button variant="filled" color={ACTIONS[pending].color} leftSection={ACTIONS[pending].icon} loading={run.isPending} onClick={() => run.mutate(pending)}>
                          {ACTIONS[pending].label}
                        </Button>
                      </Group>
                    </Group>
                  </Stack>
                </Paper>
              )}
            </Stack>
          )}

          <Divider label="Answers" labelPosition="left" />
          <EntryAnswers fields={entry.fields} answers={entry.answers as Record<string, unknown>} files={entry.files} sensitiveMasked={entry.sensitiveMasked} columns={2} />

          {entry.participants.length > 0 && (
            <>
              <Divider label={`People (${entry.participants.length})`} labelPosition="left" />
              <Stack gap={6}>
                {entry.participants
                  .slice()
                  .sort((a, b) => a.sequence - b.sequence)
                  .map((p) => (
                    <Group key={p.formEntryParticipantID} justify="space-between" wrap="nowrap">
                      <Text size="sm" fw={500}>{[p.firstName, p.surname].filter(Boolean).join(' ') || `Person ${p.sequence}`}</Text>
                      <Text size="xs" c="dimmed">{p.relationshipLabel || ''}</Text>
                    </Group>
                  ))}
              </Stack>
            </>
          )}

          <Group justify="space-between" mt="xs">
            <Button variant="subtle" color="gray" onClick={onClose}>Close</Button>
            <Button
              variant="light"
              rightSection={<IconExternalLink size={16} />}
              onClick={() => {
                onClose();
                navigate(entryPath(entry.formID, entry.formEntryID));
              }}
            >
              Open full entry
            </Button>
          </Group>
        </Stack>
      )}
    </Drawer>
  );
}
